import { loganLog } from '@RootCommon/utils/logan'
import { owlReportMetricsWeb } from '@RootCommon/utils/owl'
import { deviceHeartBeat } from '@/common/api/omReport'
import { addHeartBeatData } from './deviceHeartBeatData'
import timeoutWorker from '../timeoutWorker'
import { getNetworkInfo } from './network'
import { NETWORK_ERROR_CUSTOM_EVENT } from '@/common/context/NetworkErrorContext'
import { getLoginShopid, getWindowsAppVersion } from '@RootCommon/utils'
import { LastHeartBeatInfo } from './monitor/type'
import { HeartbeatErrorCode } from './monitor'
import { getSystemResourceInfo, getLastShutdownTime } from './deviceEventCollector'

const DEFAULT_REPORT_GAP_TIME = 30000 // 兜底30s上报一次
const LAST_HEART_BEAT_INFO_LS_KEY = 'last_heart_beat_info'

const NETWORK_TYPE_MAP = {
  '2g': 2,
  '3g': 3,
  '4g': 4,
  'slow-2g': -2,
}

let windowsAppVersion = ''

const initAppVersion = async () => {
  if (!windowsAppVersion) {
    try {
      windowsAppVersion = await getWindowsAppVersion()
    } catch (error) {
      windowsAppVersion = ''
    }
  }
}

initAppVersion()

const NO_HEART_BEAT_TIME = 3 * 60 * 1000 // 3min无心跳
const HEART_BEAT_DURATION_QUEUE_SIZE = 100 // 心跳耗时队列最大长度
let heartBeatFailedstartTime = null // "3min无心跳"的起始计算时间
let lastHeartBeatEndTime = null // 上一次心跳上报结束时间
let lastHeartBeatDuration: number | null = null // 上一次心跳上报耗时
let lastHeartBeatInfo: LastHeartBeatInfo | null = null // 上一次心跳上报信息
let heartBeatDurationQueue: number[] = [] // 心跳上报耗时队列，最大长度100

/**
 * 计算TP99值
 * @param arr 已排序的数组
 * @returns TP99值
 */
function calculateTP99 (arr: number[]): number {
  if (arr.length === 0) return 0
  if (arr.length === 1) return arr[0]

  const sortedArr = [...arr].sort((a, b) => a - b)
  const tp99Index = Math.ceil(sortedArr.length * 0.99) - 1
  return sortedArr[Math.max(0, tp99Index)]
}

/**
 * 添加心跳耗时到队列
 * @param duration 心跳上报耗时
 */
function addHeartBeatDuration (duration: number): void {
  heartBeatDurationQueue.push(duration)
  if (heartBeatDurationQueue.length > HEART_BEAT_DURATION_QUEUE_SIZE) {
    heartBeatDurationQueue.shift()
  }
}

/**
 * 获取networkLatencyTp99值
 * 队列长度小于100时取最大值，否则取TP99值
 */
function getNetworkLatencyTp99 (): number | null {
  if (heartBeatDurationQueue.length === 0) return null
  if (heartBeatDurationQueue.length < HEART_BEAT_DURATION_QUEUE_SIZE) {
    return Math.round(Math.max(...heartBeatDurationQueue))
  }
  return Math.round(calculateTP99(heartBeatDurationQueue))
}

/**
 * 心跳上报，如果3min内无心跳，则说明无网或弱网，需要通知electron展示no network页面
 */
export function reportDeviceHeartBeat (
  deviceType: number,
  heartReportType: 1 | 2 = 1,
  needReportHeartBeatData = false, // 是否需要上报心跳数据
  isFirstCall = true,
  isOnlyReportOnce = false,
) {
  const shopId = getLoginShopid()
  /**
   * 同步检查：在函数入口立即检查，避免创建不必要的executeHeartBeat和调度，节省资源
   */
  if (shopId <= 0) {
    return
  }

  const executeHeartBeat = async () => {
    // raptor上报 - 心跳上报触发数
    owlReportMetricsWeb({ device_heart_beat_trigger: 1 })
    loganLog({ msg: '心跳上报触发', shopId: shopId }, 'default', 'info', [], true)

    const startTime = Date.now()

    const connectionData = (navigator as any)?.connection
    const networkInfo = await getNetworkInfo()
    const urlTimeArr = networkInfo.urlTime
    const validDelayTimes = urlTimeArr.filter(item => item.time !== -1)
    const averageDelayTime = validDelayTimes.length > 0 ? validDelayTimes.reduce((a, b) => a + b.time, 0) / validDelayTimes.length : 99999
    let reportGapTime = DEFAULT_REPORT_GAP_TIME
    let isFailed = false

    // 获取系统资源信息和上次关闭时间
    const [systemResourceInfo, lastShutdownTime] = await Promise.all([
      getSystemResourceInfo(),
      getLastShutdownTime(),
    ])

    try {
      const res = await deviceHeartBeat(deviceType, heartReportType, {
        networkType: NETWORK_TYPE_MAP[connectionData?.effectiveType] || -2,
        networkDelay: lastHeartBeatDuration !== null ? Math.round(lastHeartBeatDuration) : Math.round(averageDelayTime),
        networkLatencyTp99: getNetworkLatencyTp99(),
        ...(needReportHeartBeatData ? (systemResourceInfo || {}) : {}),
        ...(needReportHeartBeatData && lastShutdownTime ? { lastShutdownTime } : {}),
        appVersion: windowsAppVersion,
      })
      reportGapTime = res.reportGapTime ? res.reportGapTime * 1000 : DEFAULT_REPORT_GAP_TIME
      heartBeatFailedstartTime = null
      // 心跳成功，通知在线，隐藏 NetworkError
      window.dispatchEvent(new CustomEvent(NETWORK_ERROR_CUSTOM_EVENT.ONLINE))
      // raptor上报 - 心跳上报成功率
      owlReportMetricsWeb({ device_heart_beat_success_rate: 1 }, { reasonCode: HeartbeatErrorCode.success })
      owlReportMetricsWeb({ device_heart_beat_success: 1 })
      loganLog({ msg: '心跳上报成功' }, 'default', 'info', [], true)
    } catch (e) {
      isFailed = true
      if (heartBeatFailedstartTime === null) {
        heartBeatFailedstartTime = Date.now()
      }
      if (Date.now() - heartBeatFailedstartTime >= NO_HEART_BEAT_TIME) {
        // 3min无心跳，展示 NetworkError
        window.dispatchEvent(new CustomEvent(NETWORK_ERROR_CUSTOM_EVENT.OFFLINE))
        loganLog({ msg: '3min无心跳，展示 NetworkError' }, 'default', 'info', [], true)
        // 重新计时，防止重复通知electron
        heartBeatFailedstartTime = Date.now()
      }

      // raptor上报 - 心跳上报成功率 & 心跳失败原因
      owlReportMetricsWeb({ device_heart_beat_success_rate: 0 }, {}, {
        // 先把错误原因放到extra中，运行一段时间后查看数据，再进行类型归因，以建设饼图
        extra: { error: JSON.stringify(e, Object.getOwnPropertyNames(e), 2) },
      })
      owlReportMetricsWeb({ device_heart_beat_error: 1 })
      loganLog({ msg: '心跳上报接口调用失败', error: JSON.stringify(e) }, 'default', 'info', [], true)
    }
    const endTime = Date.now()
    const responseTime = endTime - startTime
    lastHeartBeatEndTime = endTime
    lastHeartBeatDuration = responseTime
    // 添加本次心跳耗时到队列
    addHeartBeatDuration(responseTime)
    if (!isFirstCall) {
      lastHeartBeatInfo = {
        time: endTime,
        isFailed,
        isNetworkConnected: networkInfo.urlTime?.[0]?.httpCode === 200,
      }
      localStorage.setItem(LAST_HEART_BEAT_INFO_LS_KEY, JSON.stringify(lastHeartBeatInfo))
    }

    // 如果reportGapTime为空，则认为心跳失败
    needReportHeartBeatData && addHeartBeatData(startTime, responseTime, isFailed)

    if (!isOnlyReportOnce) {
      timeoutWorker.setTimeout(reportGapTime, () => {
        reportDeviceHeartBeat(deviceType, heartReportType, needReportHeartBeatData, false, false)
      })
    }
  }

  executeHeartBeat()
}
